import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@muzly/lib/utils';
import { ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

import { queueProfileSync } from '@muzly/api-client';
import { useGoBack } from '@muzly/shared';

import { PROFILE_KEYS, loadProfile, saveProfile } from './profile';
import { ProfilePhotoField } from './ProfilePhotoField';
import { toProfileBody } from './toProfileBody';
import { PageHeader } from '../../components';
import { BottomCta } from '../../layouts';
import {
  NICKNAME_MAX_LENGTH,
  NICKNAME_MIN_LENGTH,
  isNicknameValid,
  optionLabelParams,
  surveyStepById,
} from '../onboarding/survey';

import type { ProfileKey } from './profile';
import type { OnboardingAnswer } from '../onboarding/flow';
import type { SurveyStepConfig, SurveyStepId } from '../onboarding/survey';

/**
 * 프로필 수정(Figma `MY #프로필 수정`) — 닉네임과 초기 설정의 답을 확인하고 고치는 곳.
 *
 * **무엇을 고치나.** `기본 정보`(닉네임·성별·나이)와 `보컬 정보`(보컬 경험·워너비 보컬
 * 스타일). 시안이 정한 다섯 칸이다 — 이용 목적은 편집에서 빠졌고 저장된 값은 그대로 남는다
 * (초안이 저장된 답 전체에서 출발하므로 손대지 않은 키는 그대로 다시 저장된다). 측정환경·
 * 컨디션은 회차마다 묻는 답이라 프로필에 없다. 언어는 자기 화면이 있다(`LanguagePage`).
 *
 * **저장은 버튼이 한다.** 고치는 동안에는 화면 안에만 있고, 눌러야 프로필과 서버 큐에
 * 함께 남는다. 버튼은 고친 것이 있을 때만 켜지고, 되돌려 놓으면 다시 꺼진다 — 같은 값을
 * 두 번 보내지 않는다.
 *
 * 대가를 알고 고른 것이다: 안 누르고 나가면 고친 것이 사라진다. 그 대신 서버에 무엇이
 * 언제 가는지가 눈에 보인다 — 글자 하나에 요청 하나가 나가던 자동 저장을 대체한다.
 *
 * **닉네임은 2~10자.** 고친 닉네임이 범위 밖이면 저장이 꺼진다. 손대지 않은 닉네임은
 * 묻지 않는다 — 규칙이 달랐던 때 정한 이름(1자·11~12자) 때문에 다른 칸을 못 고치게
 * 막지 않는다.
 *
 * 이 화면은 탭바를 감춘다(`fullscreenPaths.ts`) — 바닥은 저장 버튼이 쓴다.
 */

const fieldsOf = (ids: SurveyStepId[]): SurveyStepConfig[] =>
  ids.flatMap((id) => surveyStepById(id) ?? []);

/** `기본 정보` 의 선택 칸 — 닉네임은 입력 칸이 따로 있다. */
const BASIC_FIELDS = fieldsOf(['gender', 'birthYear']);
const VOCAL_FIELDS = fieldsOf(['experience', 'style']);

/** 필드 카드(시안 `field/*`) — 흰 판 · 1px 테두리 · 16px 모서리. 라벨 · 값 · 밑줄. */
const FIELD_CARD =
  'relative flex flex-col gap-2 rounded-2xl border border-bk-100 bg-white px-4 pb-3.5 pt-3.5';
const FIELD_LABEL = 'text-[13px] leading-[1.4] tracking-[-0.065px] text-bk-700';
const FIELD_VALUE =
  'min-w-0 flex-1 text-base leading-[1.4] tracking-[-0.08px] text-bk-900';
const FIELD_RULE = 'h-px w-full bg-bk-100';

const FieldGroup = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="flex flex-col gap-2.5">
    <h2 className="px-1 text-sm font-medium leading-[1.4] tracking-[-0.07px] text-bk-600">
      {title}
    </h2>
    {children}
  </section>
);

/**
 * 선택형 답 하나. 보이는 것은 값 + `>` 이고, 투명한 `<select>` 가 카드 전체를 덮어 어디를
 * 눌러도 OS 의 선택 메뉴가 열린다 — 시안에 선택용 시트가 없다. 보이는 값은 장식이라
 * 보조 기술에서 가린다(값은 셀렉트가 읽는다).
 *
 * `year`는 값 하나를 고른다는 점에서 `single`과 같아 같은 셀렉트를 쓴다(survey.ts의 검증도
 * 그렇게 갈린다). 닫힌 줄의 나이는 `27세` 로만 적고, 고르는 목록은 출생연도까지 적는다.
 *
 * 라벨은 설문의 질문이 아니라 `web.profile.fields.*` 다 — 질문문은 한 화면에 하나씩 물을
 * 때의 문장이다.
 *
 * 키보드 포커스 링은 정밀 포인터(`hover:hover`)에서만 그린다. iOS WebKit 은 탭으로 연
 * 셀렉트도 `:focus-visible` 로 잡아서, 링이 터치마다 떴다 — MY 의 옛 언어 줄(같은 투명 셀렉트)에서
 * 2026-09-22 시뮬레이터로 본 것이고, 이 화면에서 다시 재지는 않았다.
 */
const SelectField = ({
  step,
  value,
  onChange,
}: {
  step: SurveyStepConfig;
  value: OnboardingAnswer | undefined;
  onChange: (value: string) => void;
}) => {
  const { t } = useTranslation();
  const current = typeof value === 'string' ? value : '';
  const selected = step.options.find((option) => option.value === current);
  const shown = !selected
    ? t('web.profile.unanswered')
    : step.kind === 'year'
      ? t('web.profile.ageValue', optionLabelParams(step, selected))
      : t(selected.labelKey, optionLabelParams(step, selected));

  return (
    <div
      className={cn(
        FIELD_CARD,
        '[@media(hover:hover)]:has-[select:focus-visible]:ring-2 [@media(hover:hover)]:has-[select:focus-visible]:ring-ring',
      )}
    >
      <label htmlFor={step.id} className={FIELD_LABEL}>
        {t(`web.profile.fields.${step.id}`)}
      </label>
      <div aria-hidden="true" className="flex items-center gap-1.5 pb-2 pt-0.5">
        <span className={cn(FIELD_VALUE, !selected && 'text-bk-600')}>
          {shown}
        </span>
        <ChevronRight className="h-5 w-5 shrink-0 text-bk-600" />
      </div>
      <div className={FIELD_RULE} />
      <select
        id={step.id}
        value={current}
        onChange={(e) => onChange(e.target.value)}
        className="absolute inset-0 h-full w-full cursor-pointer appearance-none opacity-0"
      >
        <option value="">{t('web.profile.unanswered')}</option>
        {step.options.map((option) => (
          <option key={option.value} value={option.value}>
            {t(option.labelKey, optionLabelParams(step, option))}
          </option>
        ))}
      </select>
    </div>
  );
};

export const ProfilePage = () => {
  const { t, i18n } = useTranslation();
  const goBack = useGoBack();
  /** 저장된 값. 더러움 판정의 기준선이자, 저장이 성공하면 옮겨 오는 자리다. */
  const [saved, setSaved] = useState(() => loadProfile()?.answers ?? {});
  const [draft, setDraft] = useState(saved);

  const update = (key: ProfileKey, value: OnboardingAnswer): void => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const raw = draft['nick'];
  const nick = typeof raw === 'string' ? raw : '';
  const savedNick = typeof saved['nick'] === 'string' ? saved['nick'] : '';
  // 닉네임은 앞뒤 공백을 빼고 견준다 — 공백만 붙인 것은 고친 것이 아니다.
  const nickChanged = nick.trim() !== savedNick.trim();
  /** 고친 닉네임만 규칙을 본다 — 머리 주석. */
  const nickOk = !nickChanged || isNicknameValid(nick);

  const dirty =
    nickChanged ||
    PROFILE_KEYS.some((key) => key !== 'nick' && draft[key] !== saved[key]);

  const commit = (): void => {
    // 규칙은 앞뒤 공백을 뺀 길이로 본다 — 저장도 그 값이어야 센 것과 남는 것이 같다.
    // 손대지 않은 닉네임은 저장돼 있던 그대로 둔다.
    const next = { ...draft, nick: nickChanged ? nick.trim() : saved['nick'] };
    // 완료 시각은 옮기지 않는다 — 수정은 초기 설정을 다시 마친 것이 아니다.
    const profile = saveProfile(next, loadProfile()?.completedAt);
    queueProfileSync(
      toProfileBody(next, i18n.resolvedLanguage ?? i18n.language),
    );
    setSaved(profile.answers);
    setDraft(profile.answers);
    toast.success(t('web.profile.saved'));
  };

  /**
   * 저장 안 한 수정을 들고 나가면 조용히 사라진다 — 라우터가 `<Routes>` 컴포넌트 API라
   * `useBlocker`가 없으므로(admin과 같은 제약) 나가는 자리에서 직접 묻는다.
   * 새로고침·앱 종료는 `beforeunload`가 맡는다.
   */
  const leave = (): void => {
    if (dirty && !window.confirm(t('web.profile.unsavedConfirm'))) {
      return;
    }
    void goBack();
  };

  useEffect(() => {
    if (!dirty) {
      return;
    }
    const onUnload = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  return (
    <div className="flex flex-1 flex-col">
      {/* 이 `<` 는 미저장 가드(`leave`)를 타는 유일한 문이다. */}
      <PageHeader title={t('web.profile.title')} onBack={leave} />

      <ProfilePhotoField />

      <div className="flex flex-1 flex-col gap-6 px-[18px] pb-6">
        <FieldGroup title={t('web.profile.groups.basic')}>
          {/* 입력의 포커스 표시는 카드가 진다 — 입력 자체의 outline 은 값 글자만 감싼다. */}
          <div
            className={cn(
              FIELD_CARD,
              'pb-3 has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-ring',
            )}
          >
            <label htmlFor="nick" className={FIELD_LABEL}>
              {t('web.onboarding.nick.label')}
            </label>
            <div className="flex items-center gap-1.5 pb-2 pt-0.5">
              <input
                id="nick"
                type="text"
                value={nick}
                maxLength={NICKNAME_MAX_LENGTH}
                onChange={(e) => update('nick', e.target.value)}
                placeholder={t('web.onboarding.nick.placeholder')}
                aria-describedby="nick-help"
                aria-invalid={!nickOk}
                className={cn(
                  FIELD_VALUE,
                  // 포커스로 화면이 끌려 올 때 카드 윗변·라벨이 상단 바 밑에 깔리지 않게 한다.
                  'scroll-mt-28 appearance-none bg-transparent outline-none placeholder:text-bk-600',
                )}
              />
              <span
                aria-hidden="true"
                data-testid="nick-count"
                className="shrink-0 text-[13px] leading-[1.4] tracking-[-0.065px] text-bk-600"
              >
                <span className="font-medium text-bk-900">{nick.length}</span>/
                {NICKNAME_MAX_LENGTH}
              </span>
            </div>
            <div className={cn(FIELD_RULE, !nickOk && 'bg-destructive')} />
            <p
              id="nick-help"
              className={cn(
                'text-xs leading-[1.4] tracking-[-0.06px]',
                nickOk ? 'text-bk-600' : 'text-destructive',
              )}
            >
              {t('web.profile.nickHelp', {
                min: NICKNAME_MIN_LENGTH,
                max: NICKNAME_MAX_LENGTH,
              })}
            </p>
          </div>
          {BASIC_FIELDS.map((step) => (
            <SelectField
              key={step.id}
              step={step}
              value={draft[step.id]}
              onChange={(value) => update(step.id, value)}
            />
          ))}
        </FieldGroup>

        <FieldGroup title={t('web.profile.groups.vocal')}>
          {VOCAL_FIELDS.map((step) => (
            <SelectField
              key={step.id}
              step={step}
              value={draft[step.id]}
              onChange={(value) => update(step.id, value)}
            />
          ))}
        </FieldGroup>

        <p className="whitespace-pre-line px-1 text-xs leading-[1.5] tracking-[-0.06px] text-bk-600">
          {t('web.profile.note')}
        </p>
      </div>

      {/* 버튼은 늘 서 있다 — 이 화면은 저장을 눌러야 남는 화면이고, 그 사실이 고치기
          전에도 보여야 한다. 고친 것이 있고 닉네임이 규칙 안일 때만 켜진다. */}
      <BottomCta onClick={commit} disabled={!dirty || !nickOk}>
        {t('web.profile.save')}
      </BottomCta>
    </div>
  );
};
